import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, Clock, IndianRupee, ReceiptText, Users, Eye, Shield, Building, GraduationCap } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';

export const JobCard = ({ job }) => {
    const navigate = useNavigate();

    return (
        <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden">
            <CardContent className="p-0">
                {/* Gradient accent bar */}
                <div className="h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-[#041f96]" />

                <div className="p-6">
                    {/* Header Section */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-4">
                            {/* Company Logo Placeholder */}
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                                <Building className="h-7 w-7 text-blue-600" />
                            </div>

                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <h3 className="text-xl font-bold text-[#041f96] group-hover:text-blue-600 transition-colors">
                                        {job.title}
                                    </h3>
                                    {job.posted_by_admin && (
                                        <Badge className="bg-gradient-to-r from-blue-500 via-white-500 to-purple-500 text-white border-0">
                                            <Shield className="h-3 w-3 mr-1" />
                                            Admin
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-gray-400 font-semibold flex items-center gap-1">
                                    <Shield className="h-3.5 w-3.5" />
                                    <span className="blur-[4px] select-none">Organisation Name</span>
                                    <span className="text-xs text-orange-500 font-normal ml-1">Unlock to view</span>
                                </p>
                            </div>
                        </div>

                        <Badge className={`px-3 py-1 text-sm font-medium ${job.type === 'Full-time'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-amber-100 text-amber-700 border-amber-200'
                            }`}>
                            {job.type}
                        </Badge>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center space-x-2 text-gray-600">
                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                                <MapPin className="h-4 w-4 text-gray-500" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-medium">{job.location}</span>
                                {job.distance_display && (
                                    <span className="text-xs text-blue-600 font-medium">{job.distance_display} away</span>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-600">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                                <IndianRupee className="h-4 w-4 text-green-600" />
                            </div>
                            <span className="text-sm font-medium text-green-600">{job.salary}</span>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-600">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Briefcase className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium">{job.experience}</span>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-600">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <Clock className="h-4 w-4 text-purple-600" />
                            </div>
                            <span className="text-sm font-medium">{job.posted_ago || job.posted_date}</span>
                        </div>
                    </div>

                    {/* Subjects */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {job.subjects?.map((subject, index) => (
                            <Badge key={index} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 px-3 py-1">
                                <GraduationCap className="h-3 w-3 mr-1" />
                                {subject}
                            </Badge>
                        ))}
                    </div>

                    {/* Stats & Action Row */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center space-x-4">
                            {job.purchase_count > 0 && (
                                <div className="flex items-center space-x-1.5 text-emerald-600">
                                    <Users className="h-4 w-4" />
                                    <span className="text-sm font-semibold">{job.purchase_count} applied</span>
                                </div>
                            )}
                            {job.view_count > 0 && (
                                <div className="flex items-center space-x-1.5 text-gray-500">
                                    <Eye className="h-4 w-4" />
                                    <span className="text-sm">{job.view_count} views</span>
                                </div>
                            )}
                        </div>

                        <Button
                            onClick={() => navigate(`/jobs/${job.id}`)}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
                        >
                            <ReceiptText className="h-4 w-4 mr-2" />
                            View Details
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default JobCard;
